import {
  CORE_IFRAME_COMMUNICATOR_ID,
  type BaseMessage,
  type CommunicatorOptions,
  type EventMessage,
  type ExecuteScriptPayload,
  type Message,
  type PendingRequest,
  type RequestMessage,
  type ResponseMessage,
} from './types'
import { debugLog, generateMessageId, isValidOrigin } from './utils'

export class ChildCommunicator {
  private readonly targetWindow: Window
  private readonly allowedOrigins: string[]
  private readonly requestTimeout?: number
  private readonly debug: boolean
  private readonly coreInstanceId?: string
  private readonly instanceId: string // Unique ID for this instance
  private readonly appHookIdentifier: string // Identifier for the app hook

  private pendingRequests: Map<string, PendingRequest> = new Map()
  private eventListeners: Map<string, Set<(payload: any) => void>> = new Map()
  private actionHandlers: Map<string, (payload: any) => Promise<any>> = new Map()
  private _isDestroyed = false // Renamed internal flag

  private boundHandleMessage: (event: MessageEvent) => void

  constructor(options: CommunicatorOptions) {
    if (typeof window === 'undefined' || !window.parent || window.parent === window) {
      console.warn(
        '[ChildCommunicator] Potential issue: Not running in an iframe or cannot access parent window at construction time.',
      )
    }
    this.instanceId = crypto.randomUUID()
    this.appHookIdentifier = `${this.instanceId}-iframeUrlSync` // Create unique hook identifier
    this.targetWindow = window.parent
    this.allowedOrigins = options.allowedOrigins
    this.requestTimeout = options.requestTimeout
    this.debug = options.debug ?? false
    this.coreInstanceId = options.coreInstanceId

    if (!$core) {
      console.warn('[ChildCommunicator] $core instance not found during initialization.')
    }

    this.boundHandleMessage = (event) => this.handleMessage(event)

    this.registerActionHandler('executeScript', this.handleExecuteScript.bind(this))
    this.registerActionHandler('ping', async () => 'pong')

    window.addEventListener('message', (event) => {
      // console.log(`[ChildCommunicator] Message received from parent:`, event)
      this.boundHandleMessage(event)
    })

    // ★ Register $appHook after initialization
    this.registerRouterHook()

    debugLog(
      this.debug,
      `ChildCommunicator initialized (Instance ID: ${this.instanceId})`,
      options,
    )

    this.sendEvent('childReady')

    // ★ Send initial URL after hook registration (if needed)
    // Use a small delay to ensure router might be ready?
    setTimeout(() => this.sendUrlToParent(), 1000)
  }

  // ★ Public getter for isDestroyed state
  public get isDestroyed(): boolean {
    return this._isDestroyed
  }

  // ★ Method to register the App Hook
  private registerRouterHook(): void {
    if ($core?.$appHook) {
      try {
        $core.$appHook.on(
          '$core.$router.afterEach',
          // Use an arrow function to preserve 'this' context implicitly
          (payload: { to?: any; from?: any } = {}) => {
            // Check if destroyed within the callback
            if (this.isDestroyed) return
            // console.log(`[AppHook router:afterEach] Hook triggered for ${this.instanceId}.`);
            this.sendUrlToParent()
          },
          this.appHookIdentifier, // Use unique identifier
        )
        debugLog(
          this.debug,
          `AppHook for $core.$router.afterEach registered with id: ${this.appHookIdentifier}`,
        )
      } catch (e) {
        console.error(
          `[ChildCommunicator ${this.instanceId}] Failed to register $appHook for router:afterEach:`,
          e,
        )
      }
    } else {
      console.warn(
        `[ChildCommunicator ${this.instanceId}] $core.$appHook not available, cannot register router:afterEach hook.`,
      )
    }
  }

  // ★ Method to remove the App Hook
  private removeRouterHook(): void {
    if ($core?.$appHook) {
      try {
        $core.$appHook.removeHook('$core.$router.afterEach', this.appHookIdentifier)
        debugLog(
          this.debug,
          `AppHook for $core.$router.afterEach removed with id: ${this.appHookIdentifier}`,
        )
      } catch (e) {
        console.error(
          `[ChildCommunicator ${this.instanceId}] Failed to remove $appHook for router:afterEach:`,
          e,
        )
      }
    }
  }

  public sendUrlToParent(): void {
    if (this.isDestroyed) return
    const urlData = {
      href: window.location.href,
      hash: window.location.hash,
      pathname: window.location.pathname,
      search: window.location.search,
    }
    this.sendEvent('urlChanged', urlData)
  }

  private handleMessage(event: MessageEvent): void {
    if (this.isDestroyed) return

    if (
      typeof event.data !== 'object' ||
      event.data === null ||
      event.data.serviceIdentifier !== CORE_IFRAME_COMMUNICATOR_ID
    ) {
      return
    }

    if (!isValidOrigin(event.origin, this.allowedOrigins)) {
      return // Avoid logging invalid origins frequently
    }

    const message = event.data as Message

    if (
      this.coreInstanceId &&
      message.coreInstanceId &&
      message.coreInstanceId !== this.coreInstanceId
    ) {
      return // Avoid logging mismatched instances frequently
    }

    switch (message.type) {
      case 'request':
        this.handleRequest(message as RequestMessage, event.origin)
        break
      case 'response':
        this.handleResponse(message as ResponseMessage)
        break
      case 'event':
        this.handleEvent(message as EventMessage)
        break
      default:
        console.warn(
          '[ChildCommunicator] Received unknown message type (with correct identifier):',
          message,
        )
    }
  }

  private async handleRequest(message: RequestMessage, origin: string): Promise<void> {
    const handler = this.actionHandlers.get(message.action)
    let responsePayload: any
    let errorPayload: ResponseMessage['error'] | undefined

    if (handler) {
      try {
        responsePayload = await handler(message.payload)
      } catch (error: any) {
        console.error(
          `[ChildCommunicator ${this.instanceId}] Error executing action \"${message.action}\":`,
          error,
        )
        errorPayload = {
          message: error.message || 'Unknown error during action execution',
          name: error.name,
          details: 'See child console for details',
          stack: error.stack,
        }
      }
    } else {
      errorPayload = {
        message: `Action \"${message.action}\" not found or not implemented.`,
        name: 'ActionNotFound',
      }
    }

    let plainPayload: any
    try {
      plainPayload = JSON.parse(JSON.stringify(responsePayload ?? null))
    } catch (e) {
      console.error(
        `[ChildCommunicator ${this.instanceId}] Failed to serialize response payload:`,
        e,
      )
      plainPayload = { serializationError: 'Could not serialize payload' }
    }

    const responseData: Omit<ResponseMessage, 'serviceIdentifier'> = {
      id: generateMessageId(),
      source: 'child',
      type: 'response',
      requestId: message.id,
      payload: plainPayload,
      error: errorPayload,
      coreInstanceId: this.coreInstanceId,
    }
    this.sendMessageToParent(responseData, origin)
  }

  private handleResponse(message: ResponseMessage): void {
    const pendingRequest = this.pendingRequests.get(message.requestId)
    if (pendingRequest) {
      if (pendingRequest.timeoutTimer) {
        clearTimeout(pendingRequest.timeoutTimer)
      }
      if (message.error) {
        const error = new Error(message.error.message)
        error.name = message.error.name || 'ParentError'
        ;(error as any).details = message.error.details
        pendingRequest.reject(error)
      } else {
        pendingRequest.resolve(message.payload)
      }
      this.pendingRequests.delete(message.requestId)
    }
  }

  private handleEvent(message: EventMessage): void {
    const listeners = this.eventListeners.get(message.eventName)
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(message.payload)
        } catch (error) {
          console.error(
            `[ChildCommunicator ${this.instanceId}] Error in event listener for \"${message.eventName}\":`,
            error,
          )
        }
      })
    }
  }

  // --- Public API --- //

  public sendRequest<T = any>(
    action: string,
    payload?: any,
    timeout?: number,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (this.isDestroyed || !this.targetWindow) {
        return reject(
          new Error(
            this.isDestroyed
              ? 'Communicator is destroyed.'
              : 'Parent window not available.',
          ),
        )
      }

      const requestId = generateMessageId()
      let plainPayload: any
      try {
        plainPayload = JSON.parse(JSON.stringify(payload ?? null))
      } catch (e) {
        return reject(new Error('Could not serialize request payload'))
      }

      const requestData: Omit<RequestMessage, 'serviceIdentifier'> = {
        id: requestId,
        source: 'child',
        type: 'request',
        action,
        payload: plainPayload,
        coreInstanceId: this.coreInstanceId,
      }

      const timeoutDuration = timeout ?? this.requestTimeout
      let timeoutTimer: ReturnType<typeof setTimeout> | undefined

      if (timeoutDuration) {
        timeoutTimer = setTimeout(() => {
          this.pendingRequests.delete(requestId)
          reject(
            new Error(
              `Request \"${action}\" (ID: ${requestId}) timed out after ${timeoutDuration}ms`,
            ),
          )
        }, timeoutDuration)
      }

      this.pendingRequests.set(requestId, {
        id: requestId,
        timestamp: Date.now(),
        resolve,
        reject,
        timeoutTimer,
      })

      if (this.allowedOrigins.length === 0) {
        this.pendingRequests.delete(requestId)
        if (timeoutTimer) clearTimeout(timeoutTimer)
        return reject(new Error('Cannot send request: No allowed origins specified.'))
      }
      const targetOrigin = this.allowedOrigins[0]
      this.sendMessageToParent(requestData, targetOrigin)
    })
  }

  public sendEvent(eventName: string, payload?: any): void {
    if (this.isDestroyed || !this.targetWindow) return

    let plainPayload: any
    try {
      plainPayload = JSON.parse(JSON.stringify(payload ?? null))
    } catch (e) {
      plainPayload = { serializationError: 'Could not serialize payload' }
    }

    const eventData: Omit<EventMessage, 'serviceIdentifier'> = {
      id: generateMessageId(),
      source: 'child',
      type: 'event',
      eventName,
      payload: plainPayload,
      coreInstanceId: this.coreInstanceId,
    }

    if (this.allowedOrigins.length === 0) return
    const targetOrigin = this.allowedOrigins[0]
    this.sendMessageToParent(eventData, targetOrigin)
  }

  public registerActionHandler(
    actionName: string,
    handler: (payload: any) => Promise<any>,
  ): void {
    this.actionHandlers.set(actionName, handler)
  }

  public addEventListener(
    eventName: string,
    listener: (payload: any) => void,
  ): () => void {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, new Set())
    }
    const listeners = this.eventListeners.get(eventName)!
    listeners.add(listener)

    return () => {
      listeners.delete(listener)
      if (listeners.size === 0) {
        this.eventListeners.delete(eventName)
      }
    }
  }

  public destroy(): void {
    if (this._isDestroyed) return
    this._isDestroyed = true

    window.removeEventListener('message', this.boundHandleMessage)

    // ★ Remove the specific app hook for this instance
    this.removeRouterHook()

    this.pendingRequests.forEach((request) => {
      if (request.timeoutTimer) {
        clearTimeout(request.timeoutTimer)
      }
      // Use reject to notify pending promises
      request.reject(new Error('Communicator destroyed'))
    })
    this.pendingRequests.clear()

    this.eventListeners.clear()
    this.actionHandlers.clear()

    debugLog(this.debug, `ChildCommunicator destroyed (Instance ID: ${this.instanceId})`)
  }

  // --- Private Helpers --- //

  private sendMessageToParent(
    messageData: Omit<Message, 'serviceIdentifier'>,
    targetOrigin: string,
  ): void {
    if (this._isDestroyed || !this.targetWindow) return

    try {
      // @ts-ignore
      const messageToSend: Message = {
        ...messageData,
        serviceIdentifier: CORE_IFRAME_COMMUNICATOR_ID,
      }
      this.targetWindow.postMessage(messageToSend, targetOrigin)
    } catch (error) {
      console.error(
        `[ChildCommunicator ${this.instanceId}] Error sending message to parent:`,
        error,
      )
      if (messageData.type === 'request') {
        const pending = this.pendingRequests.get(messageData.id)
        if (pending) {
          pending.reject(error)
          this.pendingRequests.delete(messageData.id)
        }
      }
    }
  }

  private async handleExecuteScript(payload: ExecuteScriptPayload): Promise<any> {
    if (!$core?.$utils?.executeStringDefinedFunction) {
      throw new Error('executeStringDefinedFunction is not available.')
    }
    if (!payload || typeof payload.functionString !== 'string') {
      throw new Error('Invalid payload for executeScript')
    }

    try {
      const result = await $core.$utils.executeStringDefinedFunction({
        functionString: payload.functionString,
        functionArgValues: payload.args || {},
        errorThrow: true,
      })
      return JSON.parse(JSON.stringify(result ?? null))
    } catch (error: any) {
      console.error(
        `[ChildCommunicator ${this.instanceId}] Error during script execution:`,
        error,
      )
      throw {
        message: `Script execution failed: ${error.message}`,
        name: error.name || 'ExecutionError',
        stack: error.stack,
        // details: 'See child console for more details.',
      }
    }
  }
}
